import { TextField } from '@mui/material';
import { PATIENT_INITIALS_VALIDATION_FEATURE_FLAG } from 'featureFlags';
import { ChangeEvent, FC, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { validatePatientInitials } from 'utils/validatePatientInitials';

type PatientInitialsInputProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  errorMessage?: string;
  size?: 'small' | 'medium';
  style?: React.CSSProperties;
  children?: React.ReactNode;
  maxLengthOverride?: number;
  required?: boolean;
  validationFeatureFlag?: boolean; // Add this prop
};

const PatientInitialsInput: FC<PatientInitialsInputProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = '',
  errorMessage = 'Invalid Input: Use either 3 letters or up to 10 digits',
  size,
  maxLengthOverride,
  required = false,
  validationFeatureFlag = PATIENT_INITIALS_VALIDATION_FEATURE_FLAG,
  style,
  children,
}) => {
  const [maxLength, setMaxLength] = useState(validationFeatureFlag ? 10 : 3);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;

      if (validationFeatureFlag) {
        if (inputValue.length === 1) {
          if (/[A-Za-z]/.test(inputValue[0])) {
            setMaxLength(3);
          } else if (/[0-9]/.test(inputValue[0])) {
            setMaxLength(10);
          }
        }

        const validatedValue = validatePatientInitials(inputValue);

        if (validatedValue !== null) {
          onChange(validatedValue);
        } else {
          toast.error(errorMessage);
        }
      } else {
        const lettersOnly = inputValue.replace(/[^A-Za-z]/g, '').toUpperCase();
        if (lettersOnly.length > 3) {
          toast.error(errorMessage);
          return;
        }
        onChange(lettersOnly);
      }
    },
    [onChange, errorMessage, validationFeatureFlag],
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', ...style }}>
      <TextField
        value={value}
        onChange={handleInputChange}
        disabled={disabled}
        placeholder={placeholder}
        inputProps={{
          maxLength: maxLengthOverride ?? maxLength,
          style: { textTransform: 'uppercase' },
        }}
        fullWidth
        size={size}
        label="Patient Initials"
        required={required}
        error={required && !value}
      />
      {children}
    </div>
  );
};

export default PatientInitialsInput;
